// Demo Mode — Pre-filled RFQ Scenario for Client Demonstrations
// Provides instant access to full Smart RFQ workflow with realistic data

import { RFQState } from './rfqConfig';

export interface DemoScenario {
  id: string;
  title: string;
  description: string;
  client: string;
  state: RFQState;
}

// Primary demo scenario: Multi-well surface testing in KSA with H₂S and high pressure
export const DEMO_SCENARIOS: DemoScenario[] = [
  {
    id: 'demo-swt-ksa-h2s',
    title: 'Surface Well Testing — Multi-Well Campaign (H₂S)',
    description: 'High-pressure well testing with sour gas in Eastern Province. Demonstrates full equipment validation, NACE compliance, safety system selection, and risk profiling.',
    client: 'Major National Operator — Eastern Province KSA',
    state: {
      service: 'surface-well-testing',
      wellheadPressure: '8500',
      oilRate: '5000',
      gasRate: '12',
      temperature: '95',
      sandProduction: 'medium',
      h2sPresence: 'yes',
      environment: 'onshore',
      duration: 'long-term',
      serviceModel: 'full-service',
      country: 'ksa',
      locationType: 'desert',
      timeline: '1-2-weeks',
      additionalNotes: 'Multi-well campaign. 5 wells planned over 60-day period. H₂S concentration measured at 150 ppm. Client requires SCADA integration for real-time data transmission. Night operations expected. Sand production intermittent but confirmed.',
      fullName: 'Demo User',
      companyName: 'EnergiPro Solutions (Demo)',
      email: 'demo@energipro.com',
      phone: '+966 50 000 0000',
    },
  },
  {
    id: 'demo-nitrogen-pipeline',
    title: 'Nitrogen Purging & Pipeline Pressure Testing',
    description: 'HP nitrogen operations for new pipeline commissioning in Abu Dhabi. Demonstrates logistics planning, mobilization validation, and execution sequence.',
    client: 'Tier-1 EPC Contractor — Abu Dhabi',
    state: {
      service: 'stimulation-nitrogen',
      wellheadPressure: '1440',
      oilRate: '0',
      gasRate: '0',
      temperature: '45',
      sandProduction: 'low',
      h2sPresence: 'no',
      environment: 'onshore',
      duration: 'short-term',
      serviceModel: 'full-service',
      country: 'uae',
      locationType: 'desert',
      timeline: 'immediate',
      additionalNotes: '18 km pipeline. New construction commissioning. Requires 3× HP nitrogen units operating simultaneously. Hydrotest to follow nitrogen purge. 24/7 operations for 14-day window. Chart recorders required for all pressure tests.',
      fullName: 'Demo User',
      companyName: 'EnergiPro Solutions (Demo)',
      email: 'demo@energipro.com',
      phone: '+971 50 000 0000',
    },
  },
  {
    id: 'demo-fabrication-skid',
    title: 'Custom Fabrication — Test Separator Skid Package',
    description: 'Engineering and fabrication of custom 3-phase test separator package for remote operations. Shows scope complexity handling.',
    client: 'International Service Company — Western Desert Egypt',
    state: {
      service: 'fabrication-skid',
      wellheadPressure: '5000',
      oilRate: '8000',
      gasRate: '25',
      temperature: '75',
      sandProduction: 'high',
      h2sPresence: 'no',
      environment: 'onshore',
      duration: 'long-term',
      serviceModel: 'rental',
      country: 'egypt',
      locationType: 'remote',
      timeline: 'planned',
      additionalNotes: 'Custom 3-phase separator skid with integrated sand management. Must handle 8,000 BOPD with high sand loading. ASME Section VIII design required. NDT (RT + UT) on all pressure welds. Client to witness FAT. Delivery to Western Desert — requires heavy transport coordination.',
      fullName: 'Demo User',
      companyName: 'EnergiPro Solutions (Demo)',
      email: 'demo@energipro.com',
      phone: '+20 10 000 0000',
    },
  },
];

// Get default demo scenario
export function getDefaultDemo(): DemoScenario {
  return DEMO_SCENARIOS[0];
}

// Get demo by ID
export function getDemoById(id: string): DemoScenario | undefined {
  return DEMO_SCENARIOS.find(d => d.id === id);
}

// Check if current URL indicates demo mode
export function isDemoMode(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('demo') === 'true';
}

// Format demo state for display
export function formatDemoSummary(scenario: DemoScenario): string {
  const s = scenario.state;
  return [
    `Service: ${scenario.title}`,
    `Client: ${scenario.client}`,
    `Conditions: ${s.wellheadPressure} PSI | ${s.oilRate} BOPD | ${s.gasRate} MMSCFD`,
    `H₂S: ${s.h2sPresence === 'yes' ? 'Present' : 'None'} | Sand: ${s.sandProduction}`,
    `Location: ${s.country.toUpperCase()} — ${s.locationType}`,
    `Model: ${s.serviceModel} | Duration: ${s.duration}`,
    `Timeline: ${s.timeline}`,
  ].join('\n');
}
